// Role-related constants

import type { PageOption, AdminPageGroup } from "@/types/role";

// Available pages that can be assigned to user roles
export const AVAILABLE_PAGES: PageOption[] = [
  { value: "dashboard", label: "Dashboard" },
  { value: "accounts", label: "Accounts" },
  { value: "billing", label: "Billing" },
  { value: "calendarnews", label: "Calendar News" },
  { value: "trading_tool", label: "Trading Tool" },
  { value: "leaderboard", label: "Leaderboard" },
  { value: "analyticsview", label: "Analytics View" },
  { value: "academy", label: "Academy" },
  { value: "ai_companion", label: "AI Companion" },
  { value: "certificates", label: "Certificates" },
  { value: "kyc", label: "KYC" },
  { value: "eSign", label: "eSign" },
];

// Admin page groups based on SideMenuAdmin structure
// Note: Values must match Prisma PageEnum (snake_case with admin_ prefix)
export const ADMIN_PAGE_GROUPS: AdminPageGroup[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    pages: [
      { value: "admin_dashboard", label: "Dashboard" },
    ],
  },
  {
    id: "meta-api-accounts",
    label: "Meta-API Accounts",
    pages: [
      { value: "admin_meta_api_accounts", label: "Trading Accounts" },
      { value: "admin_meta_api_tokens", label: "MetaApi Tokens" },
      { value: "admin_useraccount", label: "User Account" },
      { value: "admin_discounted_accounts", label: "Discounted Accounts" },
      { value: "admin_accounts", label: "MT Accounts" },
      { value: "admin_pool_accounts", label: "Account Pool" },
      { value: "admin_backup_pool_accounts", label: "Backup Account Pool" },
      { value: "admin_inactive_accounts", label: "Inactive Accounts" },
      { value: "admin_terminated_accounts", label: "Failed Accounts" },
    ],
  },
  {
    id: "vps-management",
    label: "Nexcore VPS",
    pages: [
      { value: "admin_vps", label: "VPS" },
      { value: "admin_terminals", label: "Terminals" },
      { value: "admin_nexcore_vps", label: "Nexcore VPS Admin" },
      { value: "admin_nexcore_vps_customers", label: "Nexcore VPS Customers" },
    ],
  },
  {
    id: "user",
    label: "User",
    pages: [
      { value: "admin_users", label: "Users" },
      { value: "admin_user_roles", label: "User Role" },
    ],
  },
  {
    id: "financial",
    label: "Financial Operations",
    pages: [
      { value: "admin_payments", label: "Payments" },
      { value: "admin_payouts", label: "Payouts" },
      { value: "admin_payment_discount", label: "Payment Discount" },
      { value: "admin_paytiko_settings", label: "Paytiko Settings" },
    ],
  },
  {
    id: "support",
    label: "User Support",
    pages: [
      { value: "admin_kyc", label: "KYC Management" },
      { value: "admin_support_tickets", label: "Support Tickets" },
      { value: "admin_fin_support", label: "Fin Support" },
      { value: "admin_subscribers", label: "Subscribers" },
      { value: "admin_signatures", label: "Signatures" },
    ],
  },
  {
    id: "trademate",
    label: "Trademate",
    pages: [
      { value: "admin_trademate_user_accounts", label: "User Accounts" },
      { value: "admin_trademate_accounts", label: "Trademate Accounts" },
    ],
  },
  {
    id: "trading",
    label: "Trading & Performance",
    pages: [
      { value: "admin_contracts", label: "Contracts" },
      { value: "admin_trades", label: "Trades" },
      { value: "admin_performance", label: "Performance" },
      { value: "admin_leaderboard", label: "Leaderboard" },
    ],
  },
  {
    id: "content",
    label: "Content Management",
    pages: [
      { value: "admin_certificates", label: "Certificates" },
      { value: "admin_coupons", label: "Coupons" },
      { value: "admin_promo_cards", label: "Promo Cards" },
      { value: "admin_banner_config", label: "Banner Config" },
      { value: "admin_account_banner_config", label: "Account Banner" },
      { value: "admin_extended_banner_config", label: "Extended Banner" },
      { value: "admin_popup_config", label: "Popup Config" },
      { value: "admin_promo_code_landing_page", label: "Promo Code Landing Page" },
    ],
  },
];

// Standalone pages (not in groups)
export const STANDALONE_PAGES: PageOption[] = [
  { value: "admin_user_activities", label: "User Activities" },
  { value: "admin_trading_rules", label: "Trading Rules" },
  { value: "admin_balance_check", label: "Balance Status" },
  { value: "admin_vps", label: "VPS" },
  { value: "admin_terminals", label: "Terminals" },
];

// User pageId -> path (for redirecting to first allowed user page)
export const USER_PAGE_ID_TO_PATH: Record<string, string> = {
  dashboard: "/user/dashboard",
  accounts: "/user/account",
  billing: "/user/billing",
  calendarnews: "/user/calendarnews",
  trading_tool: "/user/trading-tool",
  leaderboard: "/user/leaderboard",
  analyticsview: "/user/analyticsview",
  academy: "/user/academy",
  ai_companion: "/user/urfx-ai",
  certificates: "/user/certificates",
  kyc: "/user/kyc",
  eSign: "/user/esign",
};

// Order for picking "first" allowed user page when redirecting
export const ORDERED_USER_PAGE_IDS: string[] = [
  "dashboard", "accounts", "billing", "calendarnews", "trading_tool",
  "leaderboard", "analyticsview", "academy", "ai_companion", "certificates", "kyc", "eSign",
];

// NOTE: eSign, admin_vps, admin_terminals values match Prisma PageEnum. If you add more
// enum values in backend schema.prisma, add them here and to the relevant group above.
